/**
 * @fsd_page SelectionItem - список ресторанов в подборке
 */

import s from './SelectionItem.module.sass'

import React, { FC, useEffect } from 'react'

import { useAppDispatch, useAppSelector } from '@app/model/store'

import { loadMorePage, loadPage } from '@pages/SelectionItem/model/thunks'

import { CompilationGrid } from '@widgets/News/CompilationGrid'
import { numWord, shuffle } from '@widgets/Restaurant/Booking/lib/helper'
import { RestCard, RestCardSkeleton } from '@widgets/Restaurant/RestCards/RestCard'
import { SelectionCardCompilation, SelectionCardCompilationSkeleton } from '@widgets/Selection/SelectionCardCompilation'

import { Favorite } from '@features/Favorite'
import { Pagination } from '@features/Common/Pagination'

import { ContainerLongLeft } from '@shared/ui/Layout/Containers/ContainerLongLeft'
import { ContainerTwoColumns } from '@shared/ui/Layout/Containers/ContainerTwoColumns'
import { Cover, CoverSkeleton } from '@shared/ui/Layout/Cover'
import { SectionCarousel } from '@shared/ui/Layout/SectionCarousel'
import { Text } from '@shared/ui/Typography/Text'
import { Title } from '@shared/ui/Typography/Title'

import { AdvertSidebarSticky } from '@shared/ui/Layout/AdvertTemplate/ui/AdvertSidebarSticky'
import { AdvertTemplate } from '@shared/ui/Layout/AdvertTemplate'
import { EGG } from '@shared/api/analytics'
import { ErrorBoundary } from '@shared/lib/components/ErrorBoundary'
import { IconButton } from '@shared/ui/Actions/IconButton'
import { Rating } from '@shared/ui/Feedback/Rating'
import { targetStickersSelection } from '@shared/api/middleware/mappingAPI/patchStickersSelections'
import { TypeMappingSelectionOutput } from '@shared/api/middleware/mappingAPI/selection/type'
import { useClientResponsive } from '@shared/lib/hooks/useClientResponsive'
import SectionTitle from '@shared/ui/Layout/SectionTitle/SectionTitle'

import IconLike from '@icons/icon-like.svg'

const SelectionItem: FC = () => {
  const dispatch = useAppDispatch()

  const currentCity = useAppSelector((state) => state.global.currentCity)
  const { selection, recommended, afishaSelected } = useAppSelector((state) => state.pages.selection_item)
  const { restaurants, pagination, data } = selection
  const { isMobile, isTablet } = useClientResponsive()

  // const nextPageNumber = pagination?.page.next.number
  const currentPageNumber = pagination?.page.current.number

  const isAllPositionsSold = data?.categories?.advert?.active
  const restaurantsFormatted =
    isAllPositionsSold && selection?.restaurants?.length > 0 ? shuffle(restaurants) : restaurants
  const imgAlt = data?.description?.title
    ? `Фото подборки от Афиши — ${data.description.title}`
    : `Фото подборки от Афиши`

  let bigCoverImage = ''
  if (data?.img?.sizes) {
    bigCoverImage = isMobile ? data.img.sizes.desktop.vertical || '' : data.img.sizes.desktop.horizontal || ''
  }

  const targetStickersSlugs: string[] = Object.values(targetStickersSelection)
  const isTargetSlugForStickersSelections = Boolean(data?.url?.slug && targetStickersSlugs.includes(data.url.slug))

  if (isTargetSlugForStickersSelections && data?.img?.icon) {
    // если подборка в списке целевых, то используем иконку
    bigCoverImage = data?.img?.icon
  }

  useEffect(() => {
    EGG.pages.SelectionItem.appear_screen_page()
  }, [selection.data?.id])

  const listRestSkeleton = Array.from(Array(6).keys())

  const isShowRecommendedRests = selection.loading.page || (!!recommended && !selection.loading.page)
  const isShowAfishaSelected = selection.loading.page || (!!afishaSelected && !selection.loading.page)

  if (!data) {
    return null
  }

  return (
    <ErrorBoundary>
      <main>
        {selection.loading.page ? (
          <CoverSkeleton classNameContainer={s.cover} />
        ) : (
          <Cover img={bigCoverImage} classNameContainer={s.cover}>
            <Cover.Main className={s['cover-main']} alt={imgAlt}>
              {isAllPositionsSold && !isMobile && <Rating isPromo sizes={'M S'} className={s.promo} />}

              {/*{data.date && (*/}
              {/*  <Text tag={'span'} sizes={'M S'} className={s.date}>*/}
              {/*    {data.date}*/}
              {/*  </Text>*/}
              {/*)}*/}

              <Title sizes={'h1 h3'} className={s.title}>
                {data?.description?.title}
              </Title>

              <div className={s['cover-additional']}>
                <Text tag={'span'} sizes={'M S'}>
                  Выбор Афиши
                </Text>
                {data.description.restaurantsCount > 0 && (
                  <Text tag={'span'} sizes={'M S'}>
                    {data.description.restaurantsCount}{' '}
                    {numWord(data.description.restaurantsCount, ['ресторан', 'ресторана', 'ресторанов'])}
                  </Text>
                )}
              </div>
            </Cover.Main>

            <Cover.Aside alt={imgAlt}>
              {isAllPositionsSold && isMobile && <Rating isPromo sizes={'M S'} className={s.promo} />}

              <Favorite
                category={'selection'}
                id={selection.data.id}
                analytic={{
                  data: selection.data as TypeMappingSelectionOutput,
                  other: { section_name: 'page' },
                }}
                className={s.favorite}>
                <IconButton icon={IconLike} sizes='XL M' mode='transparent' />
              </Favorite>
              {/*// @ts-ignore*/}
            </Cover.Aside>
          </Cover>
        )}

        <ContainerTwoColumns>
          <ContainerTwoColumns.Main>
            {!!data?.description && (
              <Text sizes={'XL M'} paragraph className={s.description}>
                {data?.description.description}
              </Text>
            )}

            <div className={s['restaurants-list']} data-loading={selection.loading.items_full}>
              {restaurantsFormatted?.length
                ? restaurantsFormatted?.map((rest, index) => (
                    <RestCard
                      key={rest.id}
                      data={rest}
                      sizes={'L L L S'}
                      className={s['restaurants-item']}
                      type={'selection'}
                      analytic={{
                        advertCardPlaceType: 'selection',
                        section_name: `Подборка - ${data?.description.title}`,
                        section_index: index,
                      }}
                    />
                  ))
                : listRestSkeleton.map((el, index) => (
                    <RestCardSkeleton
                      key={index}
                      className={s['restaurants-item']}
                      sizes={'L L L S'}
                      type={'selection'}
                    />
                  ))}
            </div>

            {!!restaurantsFormatted?.length && (
              <Pagination
                mode={'all'}
                autoloadingOnMobile={true}
                currentPage={currentPageNumber ?? 0}
                // @ts-ignore
                pageCount={pagination?.page.count}
                handleLoadMore={() => dispatch(loadMorePage())}
                // @ts-ignore
                handleSelectPage={(page) => dispatch(loadPage(page))}
                analytic={{ section_name: 'Подборка' }}
                className={s.pagination}
              />
            )}

            {isShowRecommendedRests && (
              <SectionCarousel
                countSlidesOnContainer={3}
                title={'Рекомендации для вас'}
                className={s.recommended}
                skeleton={selection.loading.page}>
                <SectionCarousel.Items>
                  {recommended?.map((rest, index) => (
                    <RestCard
                      key={rest.id}
                      data={rest}
                      sizes={'L L ML M'}
                      type={'list'}
                      analytic={{
                        advertCardPlaceType: null,
                        section_name: `Рекомендации`,
                        section_index: index,
                      }}
                    />
                  )) || []}
                </SectionCarousel.Items>
                <SectionCarousel.Skeletons>
                  {Array.from(Array(4).keys()).map((i, idx) => (
                    <RestCardSkeleton key={idx} sizes={'L L ML M'} type={'list'} />
                  ))}
                </SectionCarousel.Skeletons>
              </SectionCarousel>
            )}

            <AdvertTemplate type={'100%x240 Footer'} className={s.advert} />
          </ContainerTwoColumns.Main>

          {!isMobile && !isTablet && (
            <ContainerTwoColumns.Aside>
              <AdvertTemplate type={'240x400'} container={AdvertSidebarSticky} />
              <AdvertTemplate type={'240x400_2nd'} container={AdvertSidebarSticky} />
            </ContainerTwoColumns.Aside>
          )}
        </ContainerTwoColumns>

        {isShowAfishaSelected && (
          <ContainerLongLeft classNameContent={s['afisha-selected']}>
            {/* Выбор Афиши */}
            <h2 className='visually-hidden'>Выбор Афиши, app home slider-roll, selection-card</h2>

            {isMobile ? (
              <SectionCarousel
                title={'Выбор Афиши'}
                link={currentCity?.slug ? `/${currentCity.slug}/restaurants/afisha_selected/` : ''}
                countSlidesOnContainer={3}
                skeleton={selection.loading.page}>
                <SectionCarousel.Items>
                  {
                    // @ts-ignore
                    afishaSelected?.map((item, index) => (
                      <SelectionCardCompilation
                        key={item.id}
                        data={item}
                        sizes={'M1x1'}
                        analytic={{
                          section_name: `Выбор Афиши`,
                          section_index: index,
                        }}
                      />
                    )) || []
                  }
                </SectionCarousel.Items>

                <SectionCarousel.Skeletons>
                  {Array.from(Array(6).keys()).map((i, idx) => (
                    <SelectionCardCompilationSkeleton key={idx} sizes={'M1x1'} />
                  ))}
                </SectionCarousel.Skeletons>
              </SectionCarousel>
            ) : (
              <section className={s['home-selections-grid']}>
                <SectionTitle
                  title={'Выбор Афиши'}
                  link={currentCity?.slug ? `/${currentCity.slug}/restaurants/afisha_selected/` : ''}
                  skeleton={selection.loading.page}
                />

                <CompilationGrid
                  counts={'6 6 4'}
                  type={'selections'}
                  list={afishaSelected || []}
                  analytic={{
                    section_name: `Выбор Афиши`,
                  }}
                  skeleton={selection.loading.page}
                />
              </section>
            )}
          </ContainerLongLeft>
        )}

        {!isMobile && !isTablet && (
          <AdvertTemplate type={'Content1'} container={ContainerLongLeft} className={s.advert} />
        )}
      </main>
    </ErrorBoundary>
  )
}

export default SelectionItem
